import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  deliveryFeeDeal,
  isPhoneRestricted,
  type Discount,
  type DiscountEffectiveState,
  type DiscountType,
} from '@/lib/discounts-client';
import { cn } from '@/lib/utils';

/** Tags beside a discount's name: smaller than a status badge so the name stays the headline. */
const TAG_CLASS = 'px-1.5 py-0 text-[11px] font-medium';

/**
 * State → Badge variant. Colour only where the merchant has to act or can relax: green running,
 * blue waiting to start, amber used up / not applicable, grey switched off or over.
 */
const STATE_VARIANT: Record<DiscountEffectiveState, 'success' | 'info' | 'warning' | 'muted' | 'outline'> = {
  active: 'success',
  scheduled: 'info',
  exhausted: 'warning',
  unsupported: 'warning',
  expired: 'outline',
  disabled: 'muted',
};

const STATE_DOT: Record<DiscountEffectiveState, string> = {
  active: 'bg-emerald-500',
  scheduled: 'bg-sky-500',
  exhausted: 'bg-amber-500',
  unsupported: 'bg-amber-500',
  expired: 'bg-muted-foreground/60',
  disabled: 'bg-muted-foreground/60',
};

/**
 * "Activa", "Programada", "Vencida"… as a status badge with a leading dot. The label comes from the
 * caller's namespace, because Spanish genders it (cupón activo / promoción activa).
 */
export function DiscountStateBadge({
  state,
  label,
  className,
}: {
  state: DiscountEffectiveState;
  label: string;
  className?: string;
}) {
  return (
    <Badge variant={STATE_VARIANT[state]} className={cn('gap-1.5', className)}>
      <span aria-hidden="true" className={cn('size-1.5 rounded-full', STATE_DOT[state])} />
      {label}
    </Badge>
  );
}

/** The discount's kind ("% del pedido", "Envío"…). Neutral on purpose — it describes, it does not warn. */
export function DiscountTypeBadge({ type, label }: { type: DiscountType; label: string }) {
  return (
    <Badge variant="outline" data-type={type} className="font-normal text-muted-foreground">
      {label}
    </Badge>
  );
}

/**
 * The chrome a POS-synced discount row wears, in one place.
 *
 * Both tabs and the coupon detail panel render the same three things — "Sincronizado del POS", the
 * "Acumulable" marker and the disabled Editar/Desactivar pair — and the tint strings carry their
 * `dark:` pairs, so a contrast tweak has to be a single edit or the two surfaces drift apart.
 *
 * Copy defaults to the `discounts` namespace (like `DiscountStatusPills`) so one string cannot end
 * up saying two things; the Cupones surfaces pass their own `label`/`note` because Spanish genders
 * them ("cupón sincronizado" vs "promoción sincronizada").
 */
export function PosSyncedBadge({ label }: { label?: string }) {
  const { t } = useTranslation('discounts');
  return (
    <Badge variant="warning" className={TAG_CLASS}>
      {label ?? t('pos.badge')}
    </Badge>
  );
}

/**
 * "Acumulable con otros descuentos" — and on a POS row, that the POS is the one saying so, because
 * nothing here can change it.
 */
export function StackableBadge({ fromPos = false }: { fromPos?: boolean }) {
  const { t } = useTranslation('discounts');
  return (
    <Badge variant="muted" className={TAG_CLASS}>
      {fromPos ? t('stackable.badgeFromPos') : t('stackable.badge')}
    </Badge>
  );
}

/**
 * The fee deal in words — "Envío gratis", "50% en el envío", "Incluye envío" — or null when the
 * discount leaves the fee alone. The ONE rendering of it: the badge below, the list rows' deal text,
 * the detail panel and the cart's coupon picker all read it from here.
 */
export function useDeliveryFeeDealLabel(): (
  discount: Pick<Discount, 'type' | 'value' | 'appliesToDeliveryFee'>,
) => string | null {
  const { t } = useTranslation('discounts');
  return useCallback(
    (discount) => {
      const deal = deliveryFeeDeal(discount);
      if (!deal) return null;
      if (deal.kind === 'percent') return t('deliveryFee.percent', { percent: deal.percent });
      return t(`deliveryFee.${deal.kind}`);
    },
    [t],
  );
}

/** The fee deal as a chip beside a discount's name. Renders nothing for a discount that leaves the fee alone. */
export function DeliveryFeeBadge({
  discount,
}: {
  discount: Pick<Discount, 'type' | 'value' | 'appliesToDeliveryFee'>;
}) {
  const label = useDeliveryFeeDealLabel()(discount);
  if (!label) return null;
  return (
    <Badge variant="info" className={TAG_CLASS}>
      {label}
    </Badge>
  );
}

/**
 * "Privado · 312" — the discount is limited to a phone list of that size (D5). The count only: the
 * list itself never reaches a row. Renders nothing for a discount open to everyone.
 */
export function PrivateBadge({ discount }: { discount: Pick<Discount, 'eligiblePhoneCount'> }) {
  const { t } = useTranslation('discounts');
  if (!isPhoneRestricted(discount)) return null;
  return (
    <Badge variant="soft" className={TAG_CLASS}>
      {t('eligiblePhones.badge', { phones: discount.eligiblePhoneCount })}
    </Badge>
  );
}

/**
 * Wraps the disabled actions of a POS row so hovering them explains why they are dead.
 *
 * A disabled button swallows its own pointer events, so the tooltip has to sit on a focusable
 * wrapper — without it the merchant gets a greyed-out control and no reason for it.
 */
export function ReadOnlyPosActions({
  children,
  note,
  className,
}: {
  children: React.ReactNode;
  note?: string;
  className?: string;
}) {
  const { t } = useTranslation('discounts');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className={className}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{note ?? t('pos.readOnlyNote')}</TooltipContent>
    </Tooltip>
  );
}
